<template>
    <div class="prompt-search">
        <div class="relative w-full max-w-500px">
            <!-- Search form -->
            <form @submit.prevent="submitSearch" class="relative">
                <!-- Input field with search button -->
                <div class="relative">
                    <input
                        ref="searchInput"
                        v-model="searchQuery"
                        type="text"
                        class="w-full border border-solid border-gray-300 rounded-md p-2 pr-10 focus:border-blue-400 focus:outline-none"
                        :placeholder="$t('search_prompts')"
                        @input="onInput"
                        @keydown.down.prevent="onArrowDown"
                        @keydown.up.prevent="onArrowUp"
                        @keydown.enter="onEnter"
                        @focus="onFocus"
                    />
                    <button
                        type="submit"
                        class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                        :disabled="isLoading"
                        @mousedown.prevent="submitSearchWithoutBlur"
                    >
                        <!-- Magnifying glass icon -->
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </button>
                </div>

                <!-- Loading indicator -->
                <div v-if="isLoading" class="absolute right-10 top-2">
                    <div class="spinner"></div>
                </div>
            </form>

            <!-- Dropdown for autocomplete results -->
            <div v-if="showDropdown" class="search-results">
                <div class="search-results-panel">
                    <!-- Results count -->
                    <div v-if="filteredPrompts.length > 0" class="p-2 text-sm text-gray-500">
                        {{ filteredPrompts.length }} {{ filteredPrompts.length === 1 ? 'result' : 'results' }} found
                    </div>

                    <!-- Results list -->
                    <ul v-if="filteredPrompts.length > 0" class="results-list">
                        <li
                            v-for="(prompt, index) in filteredPrompts"
                            :key="prompt.id || index"
                            class="result-item"
                            :class="{ 'result-item-selected': selectedIndex === index }"
                            @mousedown="selectPrompt(prompt)"
                            @mouseover="selectedIndex = index"
                        >
                            <div class="result-item-title">
                                <template v-for="(part, partIndex) in splitTextByQuery(prompt.title)" :key="partIndex">
                                    <span v-if="part.highlight" class="highlight-text">{{ part.text }}</span>
                                    <span v-else>{{ part.text }}</span>
                                </template>
                            </div>
                            <div class="result-item-content">
                                <template v-for="(part, partIndex) in splitTextByQuery(prompt.content)" :key="partIndex">
                                    <span v-if="part.highlight" class="highlight-text">{{ part.text }}</span>
                                    <span v-else>{{ part.text }}</span>
                                </template>
                            </div>
                        </li>
                    </ul>

                    <!-- No results message -->
                    <div v-else-if="searchQuery.length >= 2 && !isLoading && hasSearched" class="no-results">
                        {{ $t('no_results_found') || 'No results found' }}
                    </div>

                    <!-- Searching message -->
                    <div v-else-if="isLoading" class="p-2 text-sm text-gray-500 text-center">
                        {{ $t('searching') || 'Searching...' }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import { router, usePage } from '@inertiajs/vue3';

const emit = defineEmits(['update:selected']);

const searchQuery = ref('');
const filteredPrompts = ref([]);
const isLoading = ref(false);
const showDropdown = ref(false);
const selectedIndex = ref(-1);
const selectedPrompt = ref(null);
const hasSearched = ref(false); // Track if a search has been completed
const searchInput = ref(null); // Reference to the search input element
let debounceTimeout = null;
let ignoreBlur = false; // Flag to ignore blur events when clicking the search button

// Initialize search query from URL parameters on component mount
onMounted(() => {
    // Get the current URL
    const url = new URL(window.location.href);
    // Check if we're on the search page and there's a query parameter
    if (url.pathname.includes('/search') && url.searchParams.has('query')) {
        searchQuery.value = url.searchParams.get('query');
    }
});

// Custom debounce function
const debounce = (fn, delay) => {
    return (...args) => {
        if (debounceTimeout) {
            clearTimeout(debounceTimeout);
        }
        debounceTimeout = setTimeout(() => {
            fn(...args);
        }, delay);
    };
};

// Function to split text into highlighted and non-highlighted parts
const splitTextByQuery = (text) => {
    if (!text || !searchQuery.value) {
        return [{ text, highlight: false }];
    }

    try {
        // Extract search terms from the query (words of at least 2 characters)
        const searchTerms = searchQuery.value
            .toLowerCase()
            .split(' ')
            .filter(term => term.length >= 2);

        if (searchTerms.length === 0) {
            return [{ text, highlight: false }];
        }

        // Create a regex pattern that matches all search terms
        const escapedTerms = searchTerms.map(term =>
            term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        );
        const pattern = new RegExp(`(${escapedTerms.join('|')})`, 'gi');

        // Split the text by the pattern
        const parts = [];
        let lastIndex = 0;
        let match;

        // Create a copy of the text to work with
        const workingText = text || '';

        // Use exec to find all matches
        pattern.lastIndex = 0; // Reset the lastIndex
        while ((match = pattern.exec(workingText)) !== null) {
            // Add non-matching part if there is text between matches
            if (match.index > lastIndex) {
                parts.push({
                    text: workingText.substring(lastIndex, match.index),
                    highlight: false
                });
            }

            // Add matching part
            parts.push({
                text: match[0],
                highlight: true
            });

            lastIndex = pattern.lastIndex;
        }

        // Add remaining text if any
        if (lastIndex < workingText.length) {
            parts.push({
                text: workingText.substring(lastIndex),
                highlight: false
            });
        }

        return parts.length ? parts : [{ text, highlight: false }];
    } catch (error) {
        console.error('Error in splitTextByQuery:', error);
        return [{ text, highlight: false }];
    }
};

// Search function for dropdown results
const searchPrompts = async (query) => {
    if (!query || query.length < 2) {
        filteredPrompts.value = [];
        isLoading.value = false;
        hasSearched.value = false;
        showDropdown.value = false;
        return;
    }

    // Reset search state at the start of a new search
    hasSearched.value = false;
    isLoading.value = true;
    showDropdown.value = true;

    try {
        console.log('Sending search request for:', query);
        const response = await axios.get('/api/prompts/search', {
            params: { query }
        });

        console.log('Raw search response:', response);

        // Update the suggestions with the results from the server
        if (Array.isArray(response.data)) {
            filteredPrompts.value = response.data;
        } else {
            console.error('Expected array response but got:', typeof response.data);
            filteredPrompts.value = [];
        }

        console.log('Filtered prompts after update:', filteredPrompts.value);
    } catch (error) {
        console.error('Error fetching prompts:', error);
        filteredPrompts.value = [];
    } finally {
        isLoading.value = false;
        hasSearched.value = true;
        showDropdown.value = true; // Always show dropdown after search
        console.log('Search completed. hasSearched:', hasSearched.value, 'isLoading:', isLoading.value, 'results:', filteredPrompts.value.length);
    }
};

// Submit search form (when Enter is pressed or form is submitted)
// This navigates to the search results page
const submitSearch = () => {
    if (searchQuery.value.trim().length >= 2) {
        isLoading.value = true;
        showDropdown.value = false; // Hide dropdown when navigating

        // Navigate to search results page
        router.visit(`/search?query=${encodeURIComponent(searchQuery.value)}`, {
            preserveState: true,
            onSuccess: () => {
                isLoading.value = false;
            },
            onError: () => {
                isLoading.value = false;
            }
        });
    }
};

// Submit search when clicking the button (prevents blur from hiding dropdown)
const submitSearchWithoutBlur = () => {
    ignoreBlur = true; // Set flag to ignore the next blur event
    submitSearch();
};

// Input handler with debounce
const onInput = debounce(() => {
    if (searchQuery.value.trim().length >= 2) {
        searchPrompts(searchQuery.value);
    } else {
        // Hide dropdown when query is too short
        showDropdown.value = false;
        filteredPrompts.value = [];
        hasSearched.value = false;
    }
}, 300);

// Arrow key navigation
const onArrowDown = () => {
    if (filteredPrompts.value.length > 0) {
        selectedIndex.value = (selectedIndex.value + 1) % filteredPrompts.value.length;
    }
};

const onArrowUp = () => {
    if (filteredPrompts.value.length > 0) {
        selectedIndex.value = (selectedIndex.value - 1 + filteredPrompts.value.length) % filteredPrompts.value.length;
    }
};

const onEnter = () => {
    if (selectedIndex.value >= 0 && selectedIndex.value < filteredPrompts.value.length) {
        selectPrompt(filteredPrompts.value[selectedIndex.value]);
    } else {
        submitSearch();
    }
};

// Select a prompt from dropdown
const selectPrompt = (prompt) => {
    // Close the dropdown
    showDropdown.value = false;

    // Navigate to the prompt page using Inertia
    if (prompt && prompt.url) {
        router.visit(prompt.url);
    }
};

// Focus handler
const onFocus = () => {
    if (searchQuery.value.trim().length >= 2) {
        showDropdown.value = true;
    }
};

// Click outside handler
const handleClickOutside = (event) => {
    const promptSearch = document.querySelector('.prompt-search');
    if (promptSearch && !promptSearch.contains(event.target)) {
        showDropdown.value = false;
    }
};

// Add and remove event listeners
onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.prompt-search {
    width: 100%;
    display: flex;
    justify-content: center;
    position: relative;
}

.max-w-500px {
    max-width: 500px;
}

/* Search results dropdown */
.search-results {
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    margin-top: 4px;
    z-index: 9999; /* Very high z-index to ensure visibility */
}

/* Search results panel */
.search-results-panel {
    width: 100%;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    max-height: 400px;
    overflow-y: auto;
    opacity: 1 !important;
    visibility: visible !important;
    display: block !important;
}

/* Results list */
.results-list {
    list-style: none;
    padding: 0;
    margin: 0;
    width: 100%;
}

/* Result item */
.result-item {
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
    cursor: pointer;
    white-space: normal;
    display: block;
}

.result-item:hover,
.result-item-selected {
    background-color: #f1f5f9;
}

/* Result item title */
.result-item-title {
    font-size: 1rem;
    line-height: 1.5;
    font-weight: 500;
    margin-bottom: 4px;
    color: #1e293b;
}

/* Result item content */
.result-item-content {
    font-size: 0.875rem;
    color: #64748b;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

/* Highlighted text */
.highlight-text {
    background-color: #fef08a;
    font-weight: bold;
}

/* No results */
.no-results {
    padding: 16px;
    color: #64748b;
    text-align: center;
    font-size: 0.875rem;
}

/* Loading spinner */
.spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-top-color: #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}
</style>
